import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Trash2, Undo2 } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import type { ReleaseDetail, ReleaseRollbackPlan } from "../../shared/types/api";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  InlineAlert,
  LoadingState,
  StatusBadge,
} from "../../shared/ui";
import { RollbackPlanDrawer } from "./RollbackPlanDrawer";
import { RollbackStepDrawer } from "./RollbackStepDrawer";
import { ROLLBACK_STATUS_LABELS, ROLLBACK_STATUS_TONE, formatDuration } from "./releaseVocabulary";

/**
 * §39's way back.
 *
 * <p>Written before it is needed, which is the whole point. The three things this screen insists
 * on — steps, a named decision owner, and a written trigger — are the three nobody can improvise
 * at two in the morning, and a plan missing any of them fails its gate rather than passing on the
 * strength of having a heading.
 *
 * <p>Nothing here triggers a rollback. Steps are recorded as they are run, by a person who decided
 * to run them.
 */
export function ReleaseRollbackTab({
  release,
  onChanged,
}: {
  release: ReleaseDetail;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const [editingPlan, setEditingPlan] = useState(false);
  const [editingStep, setEditingStep] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  const removeStep = async (stepId: string) => {
    setRemoving(stepId);
    try {
      await api.del(`/api/v1/releases/${release.id}/rollback/steps/${stepId}`);
      onChanged();
    } finally {
      setRemoving(null);
    }
  };
  const [addingStep, setAddingStep] = useState(false);
  const [runningStep, setRunningStep] = useState<string | null>(null);

  const plan = useQuery({
    queryKey: ["release-rollback", release.id],
    queryFn: () => api.get<ReleaseRollbackPlan>(`/api/v1/releases/${release.id}/rollback`),
  });

  if (plan.isPending) {
    return <LoadingState label="Loading the rollback plan…" />;
  }

  const data = plan.data;
  if (!data) {
    return null;
  }

  const missing: string[] = [];
  if (data.steps.length === 0) {
    missing.push("steps to follow");
  }
  if (!data.decisionOwner) {
    missing.push("somebody who decides");
  }
  if (!data.triggerDescription) {
    missing.push("what would make them decide");
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Rollback plan"
          subtitle={
            data.required
              ? "Required for this release."
              : `Not required — ${data.exemptionReason}`
          }
          action={
            can("RELEASE_EDIT") && (
              <Button size="sm" variant="secondary" onClick={() => setEditingPlan(true)}>
                Edit plan
              </Button>
            )
          }
        />
        <CardBody className="space-y-3">
          {data.required && missing.length > 0 && (
            <InlineAlert tone="warning">
              This plan is still missing {missing.join(", ")}. A plan without those is a heading,
              not something anybody could follow under pressure.
            </InlineAlert>
          )}

          <div className="space-y-2 text-sm">
            <Row label="Who decides" value={data.decisionOwner?.fullName ?? "Nobody named"} />
            <Row
              label="Maximum recovery time"
              value={data.maxRecoveryMinutes == null
                ? "Not set"
                : formatDuration(data.maxRecoveryMinutes)}
            />
            <Row label="Previous artifact" value={data.previousArtifactVersion ?? "—"} />
            <Row
              label="Database rollback possible"
              value={yesNo(data.databaseRollbackPossible)}
            />
            <Row label="Migration reversible" value={yesNo(data.migrationReversible)} />
            <Row label="Backup" value={data.backupReference ?? "—"} />
          </div>

          {data.triggerDescription && (
            <div>
              <p className="text-xs font-medium text-ink-500">What would trigger a rollback</p>
              <p className="mt-0.5 whitespace-pre-wrap text-sm text-ink-800">
                {data.triggerDescription}
              </p>
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Rollback steps"
          subtitle={
            data.steps.length === 0
              ? "None yet."
              : `${data.steps.length} steps, in order`
          }
          action={
            can("RELEASE_EDIT") && (
              <Button
                size="sm"
                icon={<Plus className="h-4 w-4" />}
                onClick={() => setAddingStep(true)}
              >
                Add step
              </Button>
            )
          }
        />
        <CardBody>
          {data.steps.length === 0 ? (
            <EmptyState
              title="No rollback steps"
              message="What somebody would actually do to put things back — restore the previous image, revert the migration, restart, re-check."
              action={
                can("RELEASE_EDIT") ? (
                  <Button onClick={() => setAddingStep(true)}>Add the first step</Button>
                ) : undefined
              }
            />
          ) : (
            <ol className="divide-y divide-ink-100">
              {data.steps.map((step) => (
                <li key={step.id} className="flex items-start justify-between gap-3 py-2.5">
                  <div className="flex min-w-0 gap-2.5">
                    <span className="tnum mt-0.5 w-5 shrink-0 text-xs text-ink-400">
                      {step.sequenceNo}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink-900">{step.title}</p>
                      {step.instruction && (
                        <p className="mt-0.5 whitespace-pre-wrap text-xs text-ink-600">
                          {step.instruction}
                        </p>
                      )}
                      {step.notes && (
                        <p className="mt-1 rounded-md bg-ink-50 px-2 py-1 text-xs text-ink-700">
                          {step.notes}
                        </p>
                      )}
                      <p className="mt-0.5 text-xs text-ink-500">
                        {step.owner?.fullName ?? "Nobody assigned"}
                        {step.estimatedMinutes != null
                          && ` · ${formatDuration(step.estimatedMinutes)}`}
                        {step.executedBy && ` · run by ${step.executedBy.fullName}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <StatusBadge tone={ROLLBACK_STATUS_TONE[step.status]}>
                      {ROLLBACK_STATUS_LABELS[step.status]}
                    </StatusBadge>
                    {can("RELEASE_ROLLBACK") && step.status !== "COMPLETED" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Undo2 className="h-4 w-4" />}
                        onClick={() => setRunningStep(step.id)}
                      >
                        Record
                      </Button>
                    )}
                    {/* Only while it is still a plan. Once a step has run, its wording is the
                        account of what somebody did, and editing it would rewrite that. */}
                    {can("RELEASE_ROLLBACK") && step.status === "NOT_STARTED" && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingStep(step.id)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          loading={removing === step.id}
                          icon={<Trash2 className="h-4 w-4" />}
                          aria-label={`Remove ${step.title}`}
                          onClick={() => void removeStep(step.id)}
                        />
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </CardBody>
      </Card>

      <RollbackPlanDrawer
        release={release}
        plan={data}
        open={editingPlan}
        onClose={() => setEditingPlan(false)}
        onSaved={onChanged}
      />
      <RollbackStepDrawer
        release={release}
        plan={data}
        stepId={runningStep ?? editingStep}
        mode={editingStep ? "edit" : "record"}
        open={addingStep || runningStep != null || editingStep != null}
        onClose={() => {
          setAddingStep(false);
          setRunningStep(null);
          setEditingStep(null);
        }}
        onSaved={onChanged}
      />
    </div>
  );
}

/** Not answered is a different thing from "no", and shows as such. */
function yesNo(value: boolean | null): string {
  return value == null ? "Not recorded" : value ? "Yes" : "No";
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-ink-500">{label}</span>
      <span className="text-ink-900">{value}</span>
    </div>
  );
}
